import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { accessManagementService } from '../../services/api/accessManagementService';
import { AccessManagementState, UserPermission } from '../../types/admin/accessManagement.types';

/**
 * Combined initial state for access management
 * Manages all permission-related data in one place
 */
const initialState: AccessManagementState = {
  allUserPermissions: {
    data: [],
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
  currentUserPermissions: {
    data: null,
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch all user permissions (for admin page)
 */
export const fetchAllUserPermissions = createAsyncThunk(
  'accessManagement/fetchAllUserPermissions',
  async (_, { rejectWithValue }) => {
    try {
      return await accessManagementService.fetchUserPermissionsAPI();
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch all user permissions');
    }
  }
);

/**
 * Fetch current user permissions (single user)
 */
export const fetchCurrentUserPermissions = createAsyncThunk(
  'accessManagement/fetchCurrentUserPermissions',
  async (userAlias: string, { rejectWithValue }) => {
    try {
      return await accessManagementService.fetchSingleUserPermissionsAPI(userAlias);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch current user permissions');
    }
  }
);

/**
 * Update user permission
 */
export const updateUserPermission = createAsyncThunk(
  'accessManagement/updateUserPermission',
  async (
    {
      userAlias,
      updatedUserPermissions,
    }: {
      userAlias: string;
      updatedUserPermissions: UserPermission;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const result = await accessManagementService.updateUserPermissionAPI(userAlias, updatedUserPermissions);
      // After successful update, refetch all user permissions to get the latest data
      // AWAIT the fetch to ensure merge completes before navigation
      await dispatch(fetchAllUserPermissions()).unwrap();
      return result;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to update user permission');
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const accessManagementSlice = createSlice({
  name: 'accessManagement',
  initialState,
  reducers: {
    clearAllErrors: (state) => {
      state.allUserPermissions.error = null;
      state.currentUserPermissions.error = null;
    },
    clearAllUserPermissionsError: (state) => {
      state.allUserPermissions.error = null;
    },
    clearCurrentUserPermissionsError: (state) => {
      state.currentUserPermissions.error = null;
    },
    resetAllUserPermissions: (state) => {
      state.allUserPermissions = initialState.allUserPermissions;
    },
    resetCurrentUserPermissions: (state) => {
      state.currentUserPermissions = initialState.currentUserPermissions;
    },
    /**
     * Set current user permissions directly (for synthetic records from LDAP)
     * Used when user is in LDAP but not in DB
     */
    setCurrentUserPermissions: (state, action) => {
      state.currentUserPermissions.data = action.payload;
      state.currentUserPermissions.hasSuccessfulFetch = true;
      state.currentUserPermissions.lastFetchTimestamp = Date.now();
      state.currentUserPermissions.error = null;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchAllUserPermissions
      .addCase(fetchAllUserPermissions.pending, (state) => {
        state.allUserPermissions.isLoading = true;
        state.allUserPermissions.error = null;
        state.allUserPermissions.hasAttemptedFetch = true;
      })
      .addCase(fetchAllUserPermissions.fulfilled, (state, action) => {
        state.allUserPermissions.isLoading = false;
        // Simply use backend data - no synthetic records in allUserPermissions
        // Synthetic records are only for currentUserPermissions
        state.allUserPermissions.data = action.payload;
        state.allUserPermissions.hasSuccessfulFetch = true;
        state.allUserPermissions.lastFetchTimestamp = Date.now();
        state.allUserPermissions.error = null;
      })
      .addCase(fetchAllUserPermissions.rejected, (state, action) => {
        state.allUserPermissions.isLoading = false;
        state.allUserPermissions.error = action.payload as string;
      })
      // fetchCurrentUserPermissions
      .addCase(fetchCurrentUserPermissions.pending, (state) => {
        state.currentUserPermissions.isLoading = true;
        state.currentUserPermissions.error = null;
        state.currentUserPermissions.hasAttemptedFetch = true;
      })
      .addCase(fetchCurrentUserPermissions.fulfilled, (state, action) => {
        state.currentUserPermissions.isLoading = false;
        state.currentUserPermissions.data = action.payload;
        state.currentUserPermissions.hasSuccessfulFetch = true;
        state.currentUserPermissions.lastFetchTimestamp = Date.now();
        state.currentUserPermissions.error = null;
      })
      .addCase(fetchCurrentUserPermissions.rejected, (state, action) => {
        state.currentUserPermissions.isLoading = false;
        state.currentUserPermissions.error = action.payload as string;
      })
      // updateUserPermission
      .addCase(updateUserPermission.pending, (state) => {
        state.allUserPermissions.isLoading = true;
        state.allUserPermissions.error = null;
      })
      .addCase(updateUserPermission.fulfilled, (state) => {
        state.allUserPermissions.isLoading = false;
        // Note: allUserPermissions.data will be updated by the fetchAllUserPermissions dispatch
        // that happens after successful update
      })
      .addCase(updateUserPermission.rejected, (state, action) => {
        state.allUserPermissions.isLoading = false;
        state.allUserPermissions.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  clearAllErrors,
  clearAllUserPermissionsError,
  clearCurrentUserPermissionsError,
  resetAllUserPermissions,
  resetCurrentUserPermissions,
  setCurrentUserPermissions,
  reset,
} = accessManagementSlice.actions;

// Export reducer
export default accessManagementSlice.reducer;
